import {
  type AtomicTextureScene,
  ensureAtomicCoreTexture,
  ensureAtomicEmberTexture,
  ensureAtomicFireballTexture,
  ensureAtomicSmokeTexture,
} from './atomic-bomb-art';

/**
 * **A explosão do Núcleo Atômico: grande, esférica e vista de cima.**
 *
 * ## Sem cogumelo, e a razão é a câmera
 *
 * A câmera do KENOCYBER é superior. Um cogumelo é a silhueta de uma coluna
 * vertical vista de LADO, e de cima ele não existe - o que se vê é um disco a
 * abrir. Desenhar um cogumelo aqui seria desenhar uma coisa vista do sítio
 * errado, e é por isso que o contrato o proíbe em vez de o pedir.
 *
 * ## As cinco camadas, e a ordem em que se leem
 *
 * 1. **núcleo branco-quente** - curto. Aparece primeiro, colapsa em vez de
 *    crescer, e é o que diz "aqui";
 * 2. **bola de fogo** - amarela e laranja, a camada que dá o corpo;
 * 3. **onda de choque** - o círculo que representa o RAIO MECÂNICO. É a única
 *    camada com significado de jogo, e é a que nunca pode ser reduzida;
 * 4. **fragmentos e fumaça** - curtos, e é aqui que o LOD corta;
 * 5. **flash sobre os atingidos** - da responsabilidade da CENA, que é quem
 *    conhece os sprites dos cascos. Este sistema desenha o sítio; a cena marca
 *    quem levou.
 *
 * ## O que ela nunca faz
 *
 * Não cresce com o nível nem com o número de cópias - a escala vem do raio que
 * a AUTORIDADE mandou, e esse raio não escala com a joia. Não usa bloom global,
 * não usa um segundo renderizador e não usa pós-processamento de ecrã inteiro:
 * são quatro sprites e algumas lascas, em camadas, que é o que
 * `references/phaser-vfx.md` manda preferir para um efeito local e numeroso.
 *
 * ## O sinal mecânico nunca é reduzido
 *
 * O LOD pode tirar fumaça e fragmentos. Não pode tirar a onda de choque, não
 * pode mudar o raio, não pode mover o centro e não pode alterar quem foi
 * atingido - esses três são o que o jogador usa para perceber o que aconteceu, e
 * um LOD que os apague estaria a esconder mecânica para poupar quadros.
 */

/* -------------------------------------------------------------------------- */
/*  A cena, pela interface mínima                                             */
/* -------------------------------------------------------------------------- */

export interface AtomicBlastSprite {
  /** A largura da MOLDURA, sem escala. A conta da onda precisa dela. */
  readonly width: number;
  x: number;
  y: number;
  rotation: number;
  alpha: number;
  setDepth(value: number): AtomicBlastSprite;
  setScale(x: number, y?: number): AtomicBlastSprite;
  setVisible(value: boolean): AtomicBlastSprite;
  setBlendMode(value: unknown): AtomicBlastSprite;
  destroy(): void;
}

export interface AtomicBlastScene extends AtomicTextureScene {
  readonly tweens: {
    add(config: Record<string, unknown>): { remove(): void };
  };
  readonly add: {
    image(x: number, y: number, key: string): AtomicBlastSprite;
  };
}

/** Os modos de mistura que este sistema usa. Passados pela cena, nunca importados. */
export interface AtomicBlendModes {
  readonly add: unknown;
  readonly normal: unknown;
}

/* -------------------------------------------------------------------------- */
/*  O tempo                                                                   */
/* -------------------------------------------------------------------------- */

/** O clarão inicial. Muito curto: é um instante, não um estado. */
export const ATOMIC_CORE_MS = 180;
/** A bola de fogo, que abre e apaga. */
export const ATOMIC_FIREBALL_MS = 460;
/** A onda de choque, que chega ao raio real e desaparece nele. */
export const ATOMIC_WAVE_MS = 520;
/** A fumaça, a mais longa - e ainda assim curta. */
export const ATOMIC_SMOKE_MS = 780;
/** As lascas. */
export const ATOMIC_EMBER_MS = 520;

/** Quantas lascas uma explosão lança, no detalhe cheio. */
export const ATOMIC_EMBERS = 12;

/**
 * Quanto da onda de choque desenhada é o raio mecânico.
 *
 * **Um.** A textura da onda é um anel cuja frente fica a ~62 % do meio-lado, e o
 * chamador escala para que essa frente caia exatamente no raio que a autoridade
 * mandou. O número existe aqui para o dizer, e para uma prova o poder afirmar:
 * o círculo que o jogador vê É o raio que decidiu quem levou.
 */
export const ATOMIC_WAVE_TRUTH = 1;

/**
 * Onde a frente da onda está na textura partilhada, como fração do meio-lado.
 *
 * Exportada para a prova poder refazer a conta a partir do desenho em vez de a
 * repetir: um 0,62 escrito no teste concordaria com o teste, e não com a arte.
 */
export const ATOMIC_WAVE_FRONT_FRACTION = 0.62;

/* -------------------------------------------------------------------------- */
/*  O sistema                                                                 */
/* -------------------------------------------------------------------------- */

/** Uma explosão que a autoridade confirmou, já em unidades do mundo da cena. */
export interface AtomicBlast {
  /** O id do evento. A semente da variação, e nunca um sorteio local. */
  readonly id: number;
  readonly x: number;
  readonly y: number;
  /** O RAIO MECÂNICO, nas unidades em que a cena desenha. */
  readonly radius: number;
}

interface Live {
  readonly core: AtomicBlastSprite;
  readonly fireball: AtomicBlastSprite;
  readonly wave: AtomicBlastSprite;
  readonly smoke: AtomicBlastSprite;
  readonly embers: AtomicBlastSprite[];
  readonly tweens: { remove(): void }[];
  active: boolean;
}

export interface AtomicBlastOptions {
  /** A profundidade base. As camadas ocupam quatro valores a partir dela. */
  readonly depth?: number;
  /**
   * A chave da textura da ONDA, que é partilhada com as outras explosões.
   *
   * Passada em vez de gerada aqui: a caça já tem um anel, e um segundo anel
   * idêntico com outra chave seria a mesma imagem paga duas vezes.
   */
  readonly waveTexture: string;
  /** Os modos de mistura da cena. */
  readonly blend: AtomicBlendModes;
  /**
   * Quanta decoração desenhar, em `[0, 1]`.
   *
   * **Só corta resíduo.** Zero apaga fumaça e lascas; o núcleo, a bola de fogo e
   * a ONDA ficam sempre, porque a onda é o raio e o raio é mecânica.
   */
  readonly detail?: number;
}

/**
 * **O sistema.** Um por cena, dono de tudo o que cria.
 *
 * Reutiliza um pool: uma cadência alta com a joia encaixada produz explosões
 * seguidas, e alocar quatro sprites e doze lascas por cada uma seria alocar
 * dentro de um quadro - o que `references/phaser-vfx.md` proíbe explicitamente.
 */
export class AtomicBlastSystem {
  private readonly pool: Live[] = [];
  private disposed = false;
  private readonly coreKey: string;
  private readonly fireballKey: string;
  private readonly smokeKey: string;
  private readonly emberKey: string;

  constructor(
    private readonly scene: AtomicBlastScene,
    private readonly options: AtomicBlastOptions,
  ) {
    this.coreKey = ensureAtomicCoreTexture(scene);
    this.fireballKey = ensureAtomicFireballTexture(scene);
    this.smokeKey = ensureAtomicSmokeTexture(scene);
    this.emberKey = ensureAtomicEmberTexture(scene);
  }

  private get depth(): number {
    return this.options.depth ?? 900;
  }

  private get detail(): number {
    const value = this.options.detail ?? 1;
    return value < 0 ? 0 : value > 1 ? 1 : value;
  }

  /**
   * Desenha uma explosão confirmada.
   *
   * A deduplicação é de quem entrega - a fila da ligação e a drenagem da
   * simulação já garantem um evento uma vez - portanto este método desenha o que
   * lhe derem. Uma segunda memória aqui envelheceria ao lado da primeira.
   */
  spawn(blast: AtomicBlast): void {
    if (this.disposed || blast.radius <= 0) {
      return;
    }
    const live = this.take();
    live.active = true;
    const { x, y, radius } = blast;

    /* 1. O NÚCLEO: nasce grande e COLAPSA, para o meio ler-se como a fonte. */
    this.place(live.core, x, y, this.depth + 3, this.options.blend.add);
    live.core.setScale((radius * 1.1) / 64);
    this.push(live, {
      targets: live.core,
      scaleX: (radius * 0.28) / 64,
      scaleY: (radius * 0.28) / 64,
      alpha: 0,
      duration: ATOMIC_CORE_MS,
      ease: 'Quad.easeOut',
    });

    /* 2. A BOLA DE FOGO: abre até quase o raio e apaga. */
    this.place(live.fireball, x, y, this.depth + 2, this.options.blend.add);
    live.fireball.setScale((radius * 0.42) / 80);
    this.push(live, {
      targets: live.fireball,
      scaleX: (radius * 1.52) / 80,
      scaleY: (radius * 1.52) / 80,
      duration: ATOMIC_FIREBALL_MS,
      ease: 'Cubic.easeOut',
    });
    this.push(live, {
      targets: live.fireball,
      alpha: 0,
      duration: ATOMIC_FIREBALL_MS,
      ease: 'Quad.easeIn',
    });

    /*
     * 3. A ONDA DE CHOQUE, e ela acaba EXATAMENTE no raio mecânico.
     *
     * A frente do anel está a `ATOMIC_WAVE_FRONT_FRACTION` do meio-lado da textura,
     * portanto a escala final é `raio / (meio-lado × fração)`. É esta conta que
     * faz o círculo desenhado ser o círculo que decidiu quem levou dano, em vez
     * de uma aproximação artística dele.
     */
    this.place(live.wave, x, y, this.depth + 1, this.options.blend.add);
    const bare = this.detail <= 0;
    const waveFinal = (radius * 2) / (live.wave.width * ATOMIC_WAVE_FRONT_FRACTION);
    live.wave.setScale(waveFinal * 0.18);
    this.push(live, {
      targets: live.wave,
      scaleX: waveFinal,
      scaleY: waveFinal,
      alpha: 0,
      duration: ATOMIC_WAVE_MS,
      ease: 'Cubic.easeOut',
      /*
       * No detalhe zero a onda é a última camada viva, portanto é ela que larga
       * o slot. Um segundo tween sobre o mesmo alpha só para o largar seria dois
       * tweens a escrever no mesmo campo.
       */
      ...(bare ? { onComplete: () => this.release(live) } : {}),
    });

    if (bare) {
      return;
    }

    /* 4a. A FUMAÇA, em `NORMAL`: é a única camada que escurece. */
    this.place(live.smoke, x, y, this.depth, this.options.blend.normal);
    live.smoke.alpha = 0.85 * this.detail;
    live.smoke.setScale((radius * 0.5) / 48);
    this.push(live, {
      targets: live.smoke,
      scaleX: (radius * 1.32) / 48,
      scaleY: (radius * 1.32) / 48,
      alpha: 0,
      duration: ATOMIC_SMOKE_MS,
      ease: 'Sine.easeOut',
    });

    /*
     * 4b. Os FRAGMENTOS, lançados do centro.
     *
     * A direção de cada um vem do ID DO EVENTO e do seu índice - nunca de um
     * sorteio local - portanto seis clientes veem a mesma explosão. É a mesma
     * regra que o corte do Ciclone já segue.
     */
    const count = Math.max(0, Math.round(ATOMIC_EMBERS * this.detail));
    for (let index = 0; index < live.embers.length; index += 1) {
      const ember = live.embers[index] as AtomicBlastSprite;
      if (index >= count) {
        ember.setVisible(false);
        continue;
      }
      const angle =
        ((index + (blast.id % 7) * 0.137) / Math.max(1, count)) * Math.PI * 2 +
        (blast.id % 13) * 0.1;
      const reach = radius * (0.85 + ((blast.id + index) % 5) * 0.06);
      this.place(ember, x, y, this.depth + 2, this.options.blend.add);
      ember.rotation = angle;
      ember.setScale(1 + radius / 140);
      this.push(live, {
        targets: ember,
        x: x + Math.cos(angle) * reach,
        y: y + Math.sin(angle) * reach,
        alpha: 0,
        duration: ATOMIC_EMBER_MS,
        ease: 'Cubic.easeOut',
      });
    }

    /* O regresso ao pool acontece com a camada mais LONGA, e não antes. */
    this.push(live, {
      targets: live.smoke,
      alpha: 0,
      duration: ATOMIC_SMOKE_MS,
      onComplete: () => this.release(live),
    });
  }

  /** Quantas explosões estão vivas agora. Para as provas e para as medições. */
  liveCount(): number {
    return this.pool.filter((entry) => entry.active).length;
  }

  /** Quantos conjuntos o sistema alocou. Nunca deve crescer sem uso. */
  pooledCount(): number {
    return this.pool.length;
  }

  /**
   * Apaga tudo o que está no ecrã, sem largar os objetos.
   *
   * Para uma CORREÇÃO ou uma substituição de mundo: o que estava a ser desenhado
   * pertencia a um estado que já não existe.
   */
  clear(): void {
    for (const live of this.pool) {
      this.release(live);
    }
  }

  /**
   * Larga tudo. Os tweens saem ANTES dos sprites: um tween cujo alvo foi
   * destruído continua a correr e a escrever num objeto morto.
   */
  destroy(): void {
    this.disposed = true;
    for (const live of this.pool) {
      for (const tween of live.tweens) {
        tween.remove();
      }
      live.tweens.length = 0;
      live.core.destroy();
      live.fireball.destroy();
      live.wave.destroy();
      live.smoke.destroy();
      for (const ember of live.embers) {
        ember.destroy();
      }
      live.embers.length = 0;
    }
    this.pool.length = 0;
  }

  /* ---------------------------------------------------------------- pool -- */

  private take(): Live {
    const free = this.pool.find((entry) => !entry.active);
    if (free) {
      return free;
    }
    const live: Live = {
      core: this.spawnSprite(this.coreKey),
      fireball: this.spawnSprite(this.fireballKey),
      wave: this.spawnSprite(this.options.waveTexture),
      smoke: this.spawnSprite(this.smokeKey),
      embers: Array.from({ length: ATOMIC_EMBERS }, () => this.spawnSprite(this.emberKey)),
      tweens: [],
      active: false,
    };
    this.pool.push(live);
    return live;
  }

  private spawnSprite(key: string): AtomicBlastSprite {
    const sprite = this.scene.add.image(0, 0, key);
    sprite.setVisible(false).setDepth(this.depth);
    return sprite;
  }

  private place(
    sprite: AtomicBlastSprite,
    x: number,
    y: number,
    depth: number,
    blend: unknown,
  ): void {
    sprite.x = x;
    sprite.y = y;
    sprite.alpha = 1;
    sprite.rotation = 0;
    sprite.setDepth(depth).setBlendMode(blend).setVisible(true);
  }

  private push(live: Live, config: Record<string, unknown>): void {
    live.tweens.push(this.scene.tweens.add(config));
  }

  private release(live: Live): void {
    for (const tween of live.tweens) {
      tween.remove();
    }
    live.tweens.length = 0;
    live.active = false;
    for (const sprite of [live.core, live.fireball, live.wave, live.smoke, ...live.embers]) {
      sprite.setVisible(false);
      sprite.alpha = 0;
    }
  }
}
